import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, Menu, ShoppingBag, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { LogoImg } from "@/components/Logo";

export const Header = () => {
    const { count, openCart } = useCart();
    const { lang, setLang, t } = useLang();
    const [menuOpen, setMenuOpen] = useState(false);
    const [langOpen, setLangOpen] = useState(false);
    const langRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        setMenuOpen(false);
        setLangOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const onClick = (e) => {
            if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
        };
        document.addEventListener("click", onClick);
        return () => document.removeEventListener("click", onClick);
    }, []);

    const goAnchor = (id) => {
        if (location.pathname !== "/") {
            navigate("/");
            setTimeout(() => {
                document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
            }, 350);
        } else {
            document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
        }
    };

    const links = [
        { label: t("nav.gifts"), action: () => goAnchor("productos"), test: "nav-gifts-link" },
        { label: t("nav.lovePack"), action: () => navigate("/regalo/pack-romantico"), test: "nav-love-pack-link" },
        { label: t("nav.delivery"), action: () => goAnchor("entrega"), test: "nav-delivery-link" },
        { label: t("nav.faq"), action: () => goAnchor("faq"), test: "nav-faq-link" },
    ];

    return (
        <>
            <header
                data-testid="site-header"
                className="bg-white border-b border-stone-200"
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 sm:h-16 grid grid-cols-3 items-center">
                    <div className="flex items-center">
                        <button
                            className="lg:hidden p-2 -ml-2 text-ink"
                            onClick={() => setMenuOpen(true)}
                            data-testid="mobile-menu-button"
                            aria-label={t("nav.menu")}
                        >
                            <Menu className="h-5 w-5" strokeWidth={1.8} />
                        </button>
                        <nav className="hidden lg:flex items-center gap-7 text-sm font-medium text-stone-700">
                            {links.map((l, i) => (
                                <button
                                    key={i}
                                    onClick={l.action}
                                    data-testid={l.test}
                                    className="hover:text-bordeaux transition-colors duration-200"
                                >
                                    {l.label}
                                </button>
                            ))}
                        </nav>
                    </div>

                    <button
                        onClick={() => {
                            navigate("/");
                            window.scrollTo(0, 0);
                        }}
                        data-testid="header-logo"
                        className="justify-self-center flex items-center"
                        aria-label="Amor Sin Aviso — inicio"
                    >
                        <LogoImg className="h-8 sm:h-10 w-auto" />
                    </button>

                    <div className="flex items-center justify-end gap-1 sm:gap-2">
                        <div className="relative" ref={langRef}>
                            <button
                                onClick={() => setLangOpen(!langOpen)}
                                data-testid="language-toggle"
                                className="flex items-center gap-1 text-sm font-semibold text-stone-700 hover:text-bordeaux transition-colors px-2 py-2"
                            >
                                {lang.toUpperCase()}
                                <ChevronDown
                                    className={`h-3.5 w-3.5 transition-transform duration-200 ${langOpen ? "rotate-180" : ""}`}
                                    strokeWidth={2}
                                />
                            </button>
                            <AnimatePresence>
                                {langOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -4 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -4 }}
                                        transition={{ duration: 0.15 }}
                                        className="absolute right-0 top-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg overflow-hidden w-24"
                                    >
                                        {["es", "en"].map((l) => (
                                            <button
                                                key={l}
                                                onClick={() => {
                                                    setLang(l);
                                                    setLangOpen(false);
                                                }}
                                                data-testid={`language-toggle-${l}`}
                                                className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium hover:bg-stone-50 transition-colors"
                                            >
                                                {l === "es" ? "Español" : "English"}
                                                {lang === l && (
                                                    <Check className="h-3.5 w-3.5 text-bordeaux" strokeWidth={2.5} />
                                                )}
                                            </button>
                                        ))}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        <button
                            onClick={openCart}
                            data-testid="cart-drawer-trigger"
                            className="relative p-2 text-ink hover:text-bordeaux transition-colors"
                            aria-label={t("cart.title")}
                        >
                            <ShoppingBag className="h-5 w-5" strokeWidth={1.8} />
                            <span
                                data-testid="cart-counter"
                                className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-bordeaux text-white text-[9px] font-bold flex items-center justify-center"
                            >
                                {count}
                            </span>
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {menuOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            onClick={() => setMenuOpen(false)}
                            className="fixed inset-0 z-[60] bg-stone-900/40"
                        />
                        <motion.nav
                            initial={{ x: "-100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "-100%" }}
                            transition={{ duration: 0.28, ease: "easeOut" }}
                            className="fixed top-0 left-0 z-[61] h-full w-72 bg-white shadow-xl flex flex-col"
                            data-testid="mobile-menu"
                        >
                            <div className="h-14 px-4 flex items-center justify-between border-b border-stone-200">
                                <div className="flex items-center">
                                    <LogoImg className="h-7 w-auto" />
                                </div>
                                <button
                                    onClick={() => setMenuOpen(false)}
                                    data-testid="mobile-menu-close"
                                    aria-label={t("nav.close")}
                                    className="p-2 text-ink"
                                >
                                    <X className="h-5 w-5" strokeWidth={1.8} />
                                </button>
                            </div>
                            <div className="p-4 flex flex-col gap-1">
                                {links.map((l, i) => (
                                    <button
                                        key={i}
                                        onClick={() => {
                                            setMenuOpen(false);
                                            l.action();
                                        }}
                                        data-testid={`${l.test}-mobile`}
                                        className="text-left text-base font-medium text-stone-800 hover:text-bordeaux hover:bg-stone-50 rounded-lg px-3 py-3 transition-colors"
                                    >
                                        {l.label}
                                    </button>
                                ))}
                            </div>
                            <p className="mt-auto px-5 pb-6 text-[11px] text-stone-400">
                                {t("footer.location")}
                            </p>
                        </motion.nav>
                    </>
                )}
            </AnimatePresence>
        </>
    );
};
